import type { CSSProperties } from "react";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { getBlockName } from "@circle-react-shared/BuilderUI/Helpers/getBlockName";
import {
  useBuilderStore,
  useSelectedBlock,
} from "@circle-react-shared/BuilderUI/Hooks/useBuilderStore";
import { Typography } from "@circle-react-uikit/Typography";
import { useCanvasToolsPositionsContext } from "./CanvasToolsPositionsContext";

export interface CanvasBlockNameProps {
  "data-testid"?: string;
}

const useOffsetStyles = () => {
  const { shouldShowCanvasToolsInsideFirstBlock } = useBuilderStore(state => ({
    shouldShowCanvasToolsInsideFirstBlock:
      state.shouldShowCanvasToolsInsideFirstBlock,
  }));
  const { positions, blockActionsRect } = useCanvasToolsPositionsContext();
  const blockNameRef = useRef<HTMLDivElement>(null);
  const [blockNameWidth, setBlockNameWidth] = useState(0);

  const selectedBlockWidth = positions?.elementRect.width;
  const selectedBlockTop = positions?.elementRect.top;

  const blockActionsWidth = blockActionsRect?.width ?? 0;
  const blockActionsHeight = blockActionsRect?.height ?? 0;

  useLayoutEffect(() => {
    if (blockNameRef.current) {
      setBlockNameWidth(blockNameRef.current.getBoundingClientRect().width);
    }
  }, []);

  const isNarrowBlock = selectedBlockWidth < blockNameWidth + blockActionsWidth;

  const offsetStyles = useMemo<CSSProperties>(() => {
    if (
      shouldShowCanvasToolsInsideFirstBlock &&
      selectedBlockTop < blockActionsHeight
    ) {
      return {
        transform: "translateY(0)",
      };
    }

    if (isNarrowBlock) {
      return {
        insetInlineEnd: 0,
        transform: `translateY(calc(-100% - ${blockActionsHeight}px))`,
      };
    }

    return {
      transform: "translateY(-100%)",
    };
  }, [
    isNarrowBlock,
    shouldShowCanvasToolsInsideFirstBlock,
    selectedBlockTop,
    blockActionsHeight,
  ]);

  return {
    blockNameRef,
    isNarrowBlock,
    offsetStyles,
  };
};

export const CanvasBlockName = ({
  "data-testid": dataTestId = "canvas-block-name",
}: CanvasBlockNameProps) => {
  const { selectedBlock, meta } = useSelectedBlock();
  const { blockNameRef, offsetStyles, isNarrowBlock } = useOffsetStyles();

  const blockName = getBlockName({ block: selectedBlock, meta });

  if (!blockName) {
    return null;
  }

  return (
    <div
      className="absolute z-10"
      style={offsetStyles}
      data-testid={dataTestId}
    >
      <div
        className={classNames(
          "bg-circle pointer-events-auto my-1.5 flex items-center gap-0.5 rounded px-3 py-1 uppercase",
          {
            "mb-0": isNarrowBlock,
          },
        )}
        ref={blockNameRef}
      >
        <Typography.LabelXxs
          weight="bold"
          color="text-white"
          leading="leading-4"
          tracking="tracking-wide"
        >
          {blockName}
        </Typography.LabelXxs>
      </div>
    </div>
  );
};
